/**
 * Resource Access Configuration
 * Maps navigation items and resources to required department IDs and sub-department IDs
 * 
 * Department IDs (from auth.departments):
 *   1 = Construction
 *   2 = Accounting
 *   3 = Estimating
 *   4 = Procurement
 *   5 = Safety
 *   6 = Quality
 *   7 = Operations
 *   8 = Admin
 *   9 = Sales
 *   10 = Tool Crib
 *   11 = Resources
 * 
 * Construction Sub-Department IDs (from auth.sub_departments):
 *   1 = Piping
 *   2 = Mechanical
 *   3 = Electrical
 *   4 = Structural
 *   5 = HVAC
 *   6 = Tank (Boilermaker / Field Erected Tanks progress tracker)
 *   7 = Civil
 *   8 = Ducts
 * 
 * Resources with null/undefined = accessible to all departments/sub-departments
 * Superadmin always bypasses these checks
 */

// Maps navigation item IDs to required department IDs
// IMPORTANT: These IDs must match exactly with menuStructure.js item IDs
export const RESOURCE_DEPARTMENTS = {
  // Construction Department Resources
  'project-directory': [1],       // Construction only
  'progressTrackers': [1],        // Construction only (parent of Progress Tracking)
  'materialManagement': [1],      // Construction only
  'schedule': [1],                // Construction only
  // Model viewer: Construction + Estimating (TCs / estimators get leaf grant)
  'schedule-model': [1, 3],
  'reports': [1, 2],              // Construction + Accounting
  'forecasting': [1, 2],           // Construction + Accounting (Cost Clerk needs access for Maestro Reports)
  'forecasting-maestro-reports': [1, 2], // Explicit access for Maestro Reports
  'forecasting-billing': [1, 2],         // Explicit access for Billing (Construction + Accounting)
  'documentationManagement': [1], // Construction only (Doc's Mngmnt)
  'project-sharepoint': [1], // Project job SharePoint (sidebar root)
  'documentationManagement-sharepoint': [1], // legacy id → same access
  'project-autodesk-build': [1],
  'project-procore': [1],
  'estimate-sharepoint': [1, 3], // Estimate folder SharePoint link
  'estimate-autodesk-build': [1, 3],
  'estimate-procore': [1, 3],
  'changeManagement': [1],        // Construction only
  'technical': [1],               // Construction only
  'technical-requisitions': [1],  // Requisition log (view: project team, edit: TC)
  'qa-qc': [1],                   // Construction only (QA/QC)
  
  // Estimating Department Resources (sidebar categories + leaf IDs)
  // Category parents (Casper IA)
  'estimation-detailed': [1, 3],
  'estimation-discipline-summaries': [1, 3],
  'estimation-lookups': [1, 3],
  'estimation-indirects-group': [1, 3],
  'estimation-cost-assembly': [1, 3],
  'estimation-bid-package': [1, 3],
  // Discipline / tool leaves (IDs preserved for grants)
  'estimation-piping': [1, 3],
  'estimation-mechanical': [1, 3],
  'estimation-mechanical-equipment-history': [1, 3], // historical equipment corpus, no estimate required
  'estimation-structural': [1, 3],
  'estimation-civil': [1, 3],
  'estimation-electrical': [1, 3],
  'estimation-hvac': [1, 3],
  'estimation-sm-field': [1, 3],
  'estimation-sm-shop': [1, 3],
  'estimation-tank-shop': [1, 3],
  'estimation-boiler-mkr': [1, 3],
  'estimation-mr': [1, 3],
  'estimation-pipe-fab': [1, 3],
  'estimation-labour-rates-bank': [1, 3],
  'estimation-indirects-bank': [1, 3],
  'estimation-crew-calculation': [1, 3],
  'estimation-cost-summary': [1, 3],
  'estimation-site-expenses': [1, 3],
  'estimation-scheduling': [1, 3],    // Construction + Estimating
  'estimation-lbs': [1, 3],           // Construction + Estimating — live P6 LBS
  'estimation-evm-report': [1, 3],    // Construction + Estimating — Resource Control on the estimate
  'estimation-schedule-modules': [1, 3],
  'estimation-schedule-model': [1, 3], // Same Model viewer as Schedule → Model, on the estimate
  'schedule-modules': [1],            // Job schedule options (LBS checkbox)
  'schedule-lbs': [1],                // Job LBS (opt-in via Schedule options)
  'schedule-execution-plan': [1],     // Field execution one-pager (Yousef + Behzad only in menuAccess)
  'schedule-analysis': [1],           // DCMA / delay register (Yousef only in menuAccess)
  'schedule-msp-export': [1],          // P6 → MS Project (Construction)
  'estimation-dashboard': [1, 3],
  'estimation-ai-curve': [1, 3],
  'estimate-log': [1, 3],
  'estimation-part-rate-db': [1, 3],  // shared reference DB, no estimate required
  'estimation-rate-benchmarks': [1, 3], // cross-source rate comparison (Part Rate DB audit)
  'estimation-unit-rate': [1, 3],     // combined hourly rate calculator
  'cost-breakdown': [1, 3], // legacy parent grant still covers cost-breakdown-* leaves
  'sov': [1, 3],
  'cost-breakdown-proposal-letter': [1, 3],
  'cost-breakdown-llm-clarification-letter': [1, 3],
  'cost-breakdown-bid-form': [1, 3],
  'cost-breakdown-clarifications': [1, 3],
  'cost-breakdown-summary': [1, 3],
  'cost-breakdown-disciplines': [1, 3],
  'cost-breakdown-indirects': [1, 3],
  'cost-breakdown-site-expenses': [1, 3],
  'cost-breakdown-rentals': [1, 3],
  'cost-breakdown-labour-rates': [1, 3],
  'currency-exchange': [1, 3],
  
  // Procurement Department Resources
  'procurement': [1, 3, 4],       // Construction + Estimating + Procurement
  // Material Management dashboard: master under Procurement, reused (scoped)
  // under Estimation and Project Management. Purchasing team + TCs + PMs.
  'material-management': [1, 3, 4],
  'material-management-estimation': [1, 3, 4],
  'material-management-projects': [1, 3, 4],
  
  // Multi-Department Resources
  // Time Tracker is open to every portal user so they can log their own hours.
  // Role-based scoping controls what each user can see/edit (self/team/all).
  'actuals': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],

  // Hiring / Layoff master employee list — Accounting only (superadmin bypasses)
  'hiring-layoff': [2],
  
  // Safety / HSE Department Resources (flat root items under HSE department)
  'safety': [5],                  // legacy parent id (role templates may still grant it)
  // HSE Report (Power BI): gated in menuAccess by Manager+ OR Safety dept
  'hse-report': [1, 5],
  // Safety Training Database: Safety edits; Construction (PM/Super/GF/Foremen),
  // Accounting (Controller) and Operations management get view-only access.
  // Role templates gate who actually sees the menu item (layer 1).
  'safety-training-db': [1, 2, 5, 7],
  // WIP HSE forms — Safety dept until each dashboard ships
  'safety-jha-jsa': [5],
  'safety-toolbox-talk': [5],
  'safety-daily-inspection': [5],
  'safety-ptw-piping-mechanical': [5],
  'safety-hot-work-permit': [5],
  'safety-incident-near-miss': [5],
  'safety-certificates-db': [5],
  
  // Quality Department Resources
  'quality': [1, 6],              // Construction + Quality
  'quality-welder-continuity-log': [1, 6], // Construction + Quality (Welder Continuity Log)
  'quality-process-log': [1, 6],  // Construction + Quality (Process Log)
  
  // Resources Dashboard: Resources, Admin, Construction + any Upper Management/Manager dept
  'resources': [1, 2, 7, 8, 9, 11],
  'resources-management': [1, 2, 7, 8, 9, 11],
  // Fabrication histogram: same + Estimating (will later feed from cost breakdowns)
  'resources-fabrication': [1, 2, 3, 7, 8, 9, 11],
  // Portfolio EVM: same audience as the Corporation histogram (backend guard matches)
  'resources-portfolio-evm': [1, 2, 3, 7, 8, 9, 11],
  
  // Sales Department Resources
  'clients-database': [9],
  'bid-log': [9],
  
  // Resources available to all (no restriction)
  // 'dashboard': null,
  // 'admin': null,  // Admin panel has its own permission checks
};

/**
 * Check if a user can access a department-restricted resource
 * @param {string} resourceId - The resource/menu item ID to check
 * @param {Set|Array} userDepartmentIds - Set or Array of department IDs the user has access to
 * @returns {boolean} - True if user can access the resource
 */
export const canAccessResource = (resourceId, userDepartmentIds) => {
  const required = RESOURCE_DEPARTMENTS[resourceId];
  
  // No restriction for this resource
  if (!required || required.length === 0) {
    return true;
  }
  
  // Convert to array if it's a Set
  const userDepts = userDepartmentIds instanceof Set 
    ? Array.from(userDepartmentIds) 
    : userDepartmentIds;
  
  // Check if user has any of the required departments
  return userDepts.some(id => required.includes(id));
};

/**
 * Get the required departments for a resource
 * @param {string} resourceId - The resource/menu item ID
 * @returns {number[]|null} - Array of required department IDs, or null if no restriction
 */
export const getRequiredDepartments = (resourceId) => {
  return RESOURCE_DEPARTMENTS[resourceId] || null;
};

// =============================================================================
// SUB-DEPARTMENT ACCESS CONTROL
// =============================================================================

/**
 * Maps menu item IDs to required sub-department IDs
 * Used for Progress Tracking discipline access control
 * Users must be assigned to the specific sub-department to see that discipline
 */
export const RESOURCE_SUB_DEPARTMENTS = {
  // Progress Tracking Disciplines
  'piping-insulation': [1],           // Piping sub-department
  'mechanical-rotating-eqp': [2],     // Mechanical sub-department
  'electrical': [3],                  // Electrical sub-department
  'structure': [4],                   // Structural sub-department
  'hvac': [5],                        // HVAC sub-department
  'civil': [7],                       // Civil sub-department
  'mechanical-ducts': [8],            // Ducts sub-department
  'boilermaker': [6],                 // Tank sub-department (BMT / Field Tank)
  // 'change-orders' - No sub-department restriction (all Construction users)
};

/**
 * Check if a user can access a sub-department-restricted resource
 * @param {string} resourceId - The resource/menu item ID to check
 * @param {Set|Array} userSubDeptIds - Set or Array of sub-department IDs the user is assigned to
 * @returns {boolean} - True if user can access the resource
 */
export const canAccessSubDepartmentResource = (resourceId, userSubDeptIds) => {
  const required = RESOURCE_SUB_DEPARTMENTS[resourceId];
  
  // No sub-department restriction for this resource
  if (!required || required.length === 0) {
    return true;
  }
  
  // Convert to array if it's a Set
  const userSubDepts = userSubDeptIds instanceof Set 
    ? Array.from(userSubDeptIds) 
    : (userSubDeptIds || []);
  
  // Check if user has any of the required sub-departments
  return userSubDepts.some(id => required.includes(id));
};

/**
 * Get the required sub-departments for a resource
 * @param {string} resourceId - The resource/menu item ID
 * @returns {number[]|null} - Array of required sub-department IDs, or null if no restriction
 */
export const getRequiredSubDepartments = (resourceId) => {
  return RESOURCE_SUB_DEPARTMENTS[resourceId] || null;
};

const resourceAccess = {
  RESOURCE_DEPARTMENTS,
  RESOURCE_SUB_DEPARTMENTS,
  canAccessResource,
  canAccessSubDepartmentResource,
  getRequiredDepartments,
  getRequiredSubDepartments,
};

export default resourceAccess;
